import { create } from 'zustand';
import type { Perfil } from '../../domain/entities/Perfil';
import { PoliticaAcceso } from '../../domain/policies/PoliticaAcceso';
import {
  servicioAutenticacion,
  type CredencialesAcceso,
} from '../../services/ServicioAutenticacion';
import { ErrorApp, registrarError } from '../../domain/errores';
import { filtroDemo } from '../../data/repositories/FiltroDemo';
import { ambitoSesion } from '../../data/repositories/AmbitoSesion';
import { clienteConsultas } from '../consultas';

type EstadoArranque = 'comprobando' | 'listo' | 'incompatible';

interface EstadoSesion {
  arranque: EstadoArranque;
  perfil: Perfil | null;
  politica: PoliticaAcceso | null;

  /** Comprueba compatibilidad y recupera la sesión guardada. */
  iniciar: () => Promise<void>;
  acceder: (credenciales: CredencialesAcceso) => Promise<void>;
  accederComoDemostracion: () => Promise<void>;
  salir: () => Promise<void>;
  refrescarPerfil: () => Promise<void>;
  /** Deja el estado sin sesión sin llamar al servidor (la sesión ya se cerró fuera). */
  descartarSesion: () => void;
}

/**
 * Sesión del usuario.
 *
 * Es la única fuente de verdad sobre quién está dentro y qué puede hacer.
 * La política de acceso se recalcula junto al perfil para que nunca queden
 * desincronizados.
 */
export const useSesionStore = create<EstadoSesion>()((set) => ({
  arranque: 'comprobando',
  perfil: null,
  politica: null,

  iniciar: async () => {
    try {
      await servicioAutenticacion.verificarCompatibilidad();
    } catch (e) {
      if (e instanceof ErrorApp && e.codigo === 'BACKEND_INCOMPATIBLE') {
        set({ arranque: 'incompatible', perfil: null, politica: null });
        return;
      }
      registrarError('sesion.iniciar.compatibilidad', e);
    }

    const perfil = await servicioAutenticacion.recuperarSesion();
    ambitoSesion.establecer(perfil?.clientesVisibles ?? null);
    set({
      arranque: 'listo',
      perfil,
      politica: perfil ? new PoliticaAcceso(perfil) : null,
    });
  },

  acceder: async (credenciales) => {
    const perfil = await servicioAutenticacion.iniciarSesion(credenciales);
    ambitoSesion.establecer(perfil.clientesVisibles);
    // Nada cacheado bajo la sesión anterior vale para esta.
    clienteConsultas.clear();
    set({ perfil, politica: new PoliticaAcceso(perfil), arranque: 'listo' });
  },

  salir: async () => {
    await servicioAutenticacion.cerrarSesion();
    ambitoSesion.limpiar();
    clienteConsultas.clear();
    // Quien entre después puede no ser superadmin, o el cliente de muestra
    // puede ser otro: lo aprendido en esta sesión deja de valer.
    filtroDemo.reiniciar();
    filtroDemo.establecerActivo(false);
    set({ perfil: null, politica: null });
  },

  accederComoDemostracion: async () => {
    const perfil = await servicioAutenticacion.entrarEnModoDemostracion();
    ambitoSesion.establecer(perfil.clientesVisibles);
    clienteConsultas.clear();
    set({ perfil, politica: new PoliticaAcceso(perfil), arranque: 'listo' });
  },

  refrescarPerfil: async () => {
    const perfil = await servicioAutenticacion.refrescarPerfil();
    ambitoSesion.establecer(perfil.clientesVisibles);
    set({ perfil, politica: new PoliticaAcceso(perfil) });
  },

  descartarSesion: () => {
    filtroDemo.reiniciar();
    filtroDemo.establecerActivo(false);
    ambitoSesion.limpiar();
    clienteConsultas.clear();
    set({ perfil: null, politica: null });
  },
}));
